import { DialogContent, DialogProps } from '@material-ui/core';
import { FormSelectOrCreateCompanyFieldWithSelector } from 'app/main/contacts/companies/forms/FormSelectOrCreateCompanyFieldWithSelector';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { FullScreenDialogWithStepper } from 'framework/dialogs/FullScreenDialogWithStepper';
import { FormDatePickerWithUnit } from 'framework/forms/FormDatePickerWithUnit';
import { FormSingleCheckboxField } from 'framework/forms/FormSingleCheckboxField';
import { FormTextField } from 'framework/forms/FormTextField';
import { PageableFormDialogActions } from 'framework/forms/PageableFormDialogActions';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { ITenderModel, patientsQuery_single, tendersCommand_new, tendersQuery_nextReference, tendersQuery_settings, ValidUntilTimeUnits } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo, useState } from 'react';
import { FormRecipientField } from 'shared/forms/FormRecipientField';
import { useNextReferenceInterceptor } from 'shared/interceptors/useNextReferenceInterceptor';
import { useWithRecipientInterceptor } from 'shared/searchAndSelect/useWithRecipientInterceptor';
import * as yup from 'yup';
import { SearchProvider } from './context/SearchProvider';
import { ILine } from './ILine';
import { SearchAndSelectProductsForTender } from './SearchAndSelectProductsForTender';
import { lineToModel } from './utils/lineToModel';
import { useApiEffectWithDefer } from 'framework/hooks/useApiEffectWithDefer';
import { isNotNullNorUndefined } from 'framework/utils/nullNorUndefinedCheck';

const createSchema = (strings: IStrings, mustSelectCompany: boolean) => {
	return yup
		.object<ITenderModel>({
			validUntil: yup.date().required(),
			lines: yup.mixed(),
			reference: yup.string().required(),
			isAutoSelectReference: yup.boolean().defined(),
			patientId: yup.string(),
			zip: yup.string(),
			addressLine: yup.string(),
			city: yup.string(),
			companyId: mustSelectCompany ? yup.string().required() : yup.string(),
			country: yup.string(),
			recipientName: yup.string().required(),
		})
		.defined();
};

const EmptyValues: ITenderModel = {
	lines: [],
	reference: '',
	isAutoSelectReference: true,
	patientId: '',
	city: '',
	companyId: '',
	addressLine: '',
	zip: '',
	country: '',
	recipientName: '',
	validUntil: undefined as any,
};

const stepsRecord: Record<number, (keyof ITenderModel)[]> = {
	0: ['reference', 'isAutoSelectReference', 'validUntil'],
	1: ['companyId'],
	2: ['recipientName', 'addressLine', 'city', 'zip', 'country'],
	3: ['lines'],
};

interface IProps extends DialogProps {
	confirm: (id: string) => void;
	cancel: VoidFunction;
	patientId?: string;
}

export const TenderModelForm = ({ confirm, cancel, patientId, ...rest }: IProps) => {
	const strings = useLocalization();
	const [lines, setLines] = useState<ILine[]>([]);
	const [step, setStep] = useState<number>(0);
	const [isSelectCompany, setIsSelectCompany] = useState<boolean>(false);
	const [create, isSubmitting] = useFormSubmit(tendersCommand_new);
	const notify = useSnackbarNotify();
	const schema = useMemo(() => createSchema(strings, isSelectCompany), [strings, isSelectCompany]);

	const handleSubmit = async (values: ITenderModel, helpers: FormikHelpers<ITenderModel>) => {
		if (lines.length === 0) {
			notify(strings.selectAtLeastOneWhat(strings.line), 'error');
		} else {
			const r = await create({ ...values, lines: lines.map((t, index) => ({ index, ...t })).map(lineToModel) });
			if (handleFormResponse(r, helpers, stepsRecord, setStep)) {
				notify(strings.addedWhat(strings.tender));
				confirm(r.result.objectId);
			}
		}
	};

	return (
		<Formik<ITenderModel>
			validateOnMount
			initialValues={{ ...EmptyValues, patientId: patientId ?? '' }}
			validationSchema={schema}
			onSubmit={handleSubmit}>
			<Form>
				<SearchProvider>
					<InnerDialog
						{...rest}
						isSubmitting={isSubmitting}
						cancel={cancel}
						lines={lines}
						setLines={setLines}
						step={step}
						setStep={setStep}
						stepsRecord={stepsRecord}
						isSelectCompany={isSelectCompany}
						setIsSelectCompany={setIsSelectCompany}
						schema={schema}
						patientId={patientId}
					/>
				</SearchProvider>
			</Form>
		</Formik>
	);
};

interface IInnerDialogProps extends DialogProps {
	cancel: VoidFunction;
	lines: ILine[];
	setLines: React.Dispatch<React.SetStateAction<ILine[]>>;
	isSubmitting: boolean;
	step: number;
	setStep: React.Dispatch<React.SetStateAction<number>>;
	stepsRecord: Record<number, (keyof ITenderModel)[]>;
	isSelectCompany: boolean;
	setIsSelectCompany: React.Dispatch<React.SetStateAction<boolean>>;
	schema: yup.ObjectSchema<ITenderModel>;
	patientId: string | undefined;
}

const InnerDialog = ({
	cancel,
	lines,
	setLines,
	isSubmitting,
	step,
	setStep,
	stepsRecord,
	isSelectCompany,
	setIsSelectCompany,
	schema,
	patientId,
	...rest
}: IInnerDialogProps) => {
	const props = useFormikContext<ITenderModel>();
	const strings = useLocalization();
	useNextReferenceInterceptor(tendersQuery_nextReference, props);
	const [settings] = useApiEffect(tendersQuery_settings);
	const [isUseContactPerson, setIsUseContactPerson] = useState<boolean>(false);
	useWithRecipientInterceptor<ITenderModel>(props, isSelectCompany, isUseContactPerson, patientId);
	const [patient] = useApiEffectWithDefer(patientsQuery_single, patientId!, val => isNotNullNorUndefined(val));

	if (settings === undefined) {
		return <div></div>;
	}

	return (
		<FullScreenDialogWithStepper
			{...rest}
			fullScreenStep={3}
			title={strings.newWhat(strings.tender)}
			step={step}
			labels={[strings.referenceShort, strings.vatCustomer, strings.withRespectToShort, strings.items]}
			maxWidth='lg'>
			<DialogContent
				dividers
				className='df-col fg1'>
				{step === 0 && (
					<>
						<FormSingleCheckboxField<ITenderModel>
							name='isAutoSelectReference'
							label={strings.autoSelectIndexNumberQuestion}
						/>
						<FormTextField<ITenderModel>
							name='reference'
							label={strings.reference}
							disabled={props.values.isAutoSelectReference}
						/>
						<FormDatePickerWithUnit<ITenderModel>
							name='validUntil'
							label={strings.validTo}
							required
							units={ValidUntilTimeUnits}
							defaultUnit='Months'
							defaultValue={settings.defaultValidUntil}
						/>
					</>
				)}
				{step === 1 && (
					<FormSelectOrCreateCompanyFieldWithSelector<ITenderModel>
						isSelectCompany={isSelectCompany}
						setIsSelectCompany={setIsSelectCompany}
						patientId={patientId}
					/>
				)}
				{step === 2 && (
					<FormRecipientField<ITenderModel>
						patientId={patientId}
						isUseContactPerson={isUseContactPerson}
						setIsUseContactPerson={setIsUseContactPerson}
					/>
				)}
				{step === 3 && (
					<SearchAndSelectProductsForTender
						lines={lines}
						setLines={setLines}
						patient={patient}
					/>
				)}
			</DialogContent>
			<PageableFormDialogActions
				step={step}
				setStep={setStep}
				cancel={cancel}
				isSubmitting={isSubmitting}
				submitText={strings.create}
				schema={schema}
				stepsRecord={stepsRecord}
			/>
		</FullScreenDialogWithStepper>
	);
};
